import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowUpRight,
  Star,
  Crown,
  ChevronRight,
  Leaf,
  Gem,
  Heart,
} from 'lucide-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import SectionHeading from './SectionHeading'
import ApiURl from '../controllers/Api'

gsap.registerPlugin(ScrollTrigger)

/*
 * Each product carries the extra copy needed to build
 * the premium "Top Products" card.
 *
 * ribbon   = ranking number
 * featured = #1 / bestseller product
 */
const fallbackProducts = [
  {
    id: 'fallback-bangle',
    name: 'Bangle',
    displayName: 'Classic Gold Bangles',
    image:
      'https://images.unsplash.com/photo-1567567557645-8450247d194a?q=85&w=1000&auto=format&fit=crop',
    subtitle: 'Forever classic. Always in style.',
    rating: 4.7,
    reviews: 389,
    ribbon: 2,
    features: [
      { icon: Leaf, label: 'Pure Gold' },
      { icon: Gem, label: 'Elegant Finish' },
      { icon: Heart, label: 'Trusted Quality' },
    ],
  },
  {
    id: 'fallback-earrings',
    name: 'Earrings',
    displayName: 'Royal Gold Jhumkas',
    image:
      'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=85&w=1000&auto=format&fit=crop',
    subtitle: 'A touch of tradition. A whole lot of grace.',
    rating: 4.8,
    reviews: 430,
    ribbon: 1,
    featured: true,
    features: [
      { icon: Leaf, label: '92.5% Pure Gold' },
      { icon: Gem, label: 'Handcrafted Design' },
      { icon: Heart, label: 'Customer Favorite' },
    ],
  },
  {
    id: 'fallback-necklace',
    name: 'Necklace',
    displayName: 'Heritage Gold Necklace',
    image:
      'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?q=85&w=1000&auto=format&fit=crop',
    subtitle: 'Timeless elegance for every occasion.',
    rating: 4.9,
    reviews: 512,
    ribbon: 3,
    features: [
      { icon: Leaf, label: 'Pure Gold' },
      { icon: Gem, label: 'Crafted with Care' },
      { icon: Heart, label: 'Loved by Thousands' },
    ],
  },
]

const MAROON_DEEP = 'var(--user-maroon, #2B0000)'
const GOLD = 'var(--user-gold, #D4AF37)'
const GOLD_SOFT = 'var(--user-gold-soft, #E9CD7A)'
const CREAM = 'var(--user-cream, #ded6c8)'
const NAV_BACKGROUND = 'var(--user-bg, #E8D9D5)'
const CARD_CREAM = '#FBF6EC'

const FALLBACK_FEATURES = [
  { icon: Leaf, label: 'Pure Gold' },
  { icon: Gem, label: 'Elegant Finish' },
  { icon: Heart, label: 'Trusted Quality' },
]

const stripHtml = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const getRatingValue = (product) =>
  Number(
    product.average_rating ||
      product.avg_rating ||
      product.rating ||
      0
  )

const getReviewCount = (product) =>
  Number(
    product.review_count ||
      product.reviews_count ||
      product.reviews ||
      0
  )

const shuffleProducts = (list) =>
  [...list]
    .map((item) => ({
      item,
      sort: Math.random(),
    }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item)

const getProductImage = (product) => {
  const firstApiImage = Array.isArray(product.images)
    ? product.images[0]?.url
    : ''

  if (firstApiImage) {
    return firstApiImage
  }

  const rawImagePath = String(product.img_path || '').trim()

  if (!rawImagePath) {
    return fallbackProducts[0].image
  }

  if (/^https?:\/\//i.test(rawImagePath)) {
    return rawImagePath
  }

  const firstFilename = rawImagePath
    .split(/[|,]/)
    .map((item) => item.trim())
    .filter(Boolean)[0]

  return firstFilename
    ? `${ApiURl}images/${encodeURIComponent(firstFilename)}`
    : fallbackProducts[0].image
}

const mapApiProductToCard = (product, index) => {
  const name =
    product.product_name ||
    product.name ||
    `Product ${product.product_id || index + 1}`

  const description =
    stripHtml(product.description) ||
    product.category_name ||
    'Explore premium jewellery crafted with timeless detail.'

  return {
    id: product.product_id || product.id || name,
    name,
    displayName: name,
    image: getProductImage(product),

    subtitle:
      description.length > 95
        ? `${description.slice(0, 95)}...`
        : description,

    rating: getRatingValue(product),
    reviews: getReviewCount(product),

    ribbon: index + 1,

    // Product ranking #1 becomes featured
    featured: index === 0,

    features: FALLBACK_FEATURES,
  }
}

/* =========================================================
   STAR RATING
========================================================= */

function StarRow({ rating, size = 'h-3.5 w-3.5' }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => {
        const filled = i < Math.round(rating)

        return (
          <Star
            key={i}
            className={size}
            style={{
              fill: filled ? GOLD : 'transparent',
              color: filled ? GOLD : 'rgba(43,0,0,0.25)',
              strokeWidth: filled ? 0 : 1.5,
            }}
          />
        )
      })}
    </div>
  )
}

/* =========================================================
   RANKING RIBBON
========================================================= */

function RibbonBadge({ number, size = 'desktop' }) {
  const dims =
    size === 'desktop'
      ? 'h-11 w-10'
      : 'h-9 w-8'

  const textSize =
    size === 'desktop'
      ? 'text-base'
      : 'text-sm'

  return (
    <div
      data-anim="card-ribbon"
      className={`absolute left-3 top-0 z-20 ${dims}`}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: MAROON_DEEP,
          clipPath:
            'polygon(0 0, 100% 0, 100% 76%, 50% 100%, 0 76%)',
          boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
        }}
      />

      <span
        className={`relative z-10 flex h-full w-full items-center justify-center pb-1.5 font-serif font-bold ${textSize}`}
        style={{
          color: GOLD_SOFT,
        }}
      >
        {number}
      </span>
    </div>
  )
}

/* =========================================================
   COMPONENT
========================================================= */

export default function ProductCategories() {
  const navigate = useNavigate()
  const sectionRef = useRef(null)
  const cardsContainerRef = useRef(null)

  const [topProducts, setTopProducts] =
    useState(fallbackProducts)

  /*
   * API order:
   *
   * topProducts[0] = #1
   * topProducts[1] = #2
   * topProducts[2] = #3
   *
   * Desktop visual order:
   *
   * #2   #1   #3
   */
  const desktopProducts =
    topProducts.length >= 3
      ? [
          topProducts[1],
          topProducts[0],
          topProducts[2],
          ...topProducts.slice(3),
        ]
      : topProducts

  const handleProductOpen = (productId) => {
    if (!productId || String(productId).startsWith('fallback-')) {
      navigate('/all-products')
      return
    }

    navigate(`/ProductOverview/${productId}`)
  }

  const handleProductKeyDown = (event, productId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleProductOpen(productId)
    }
  }

  /* =======================================================
     LOAD PRODUCTS
  ======================================================= */

  useEffect(() => {
    let mounted = true

    fetch(`${ApiURl}getallproducts.php`)
      .then((response) => response.json())
      .then((response) => {
        if (!mounted) {
          return
        }

        const apiProducts = Array.isArray(response?.products)
          ? response.products
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : []

        if (!apiProducts.length) {
          return
        }

        const hasRatedProducts = apiProducts.some(
          (product) =>
            getRatingValue(product) > 0 ||
            getReviewCount(product) > 0
        )

        const selectedProducts = hasRatedProducts
          ? [...apiProducts].sort((a, b) => {
              const ratingDifference =
                getRatingValue(b) -
                getRatingValue(a)

              if (ratingDifference !== 0) {
                return ratingDifference
              }

              return (
                getReviewCount(b) -
                getReviewCount(a)
              )
            })
          : shuffleProducts(apiProducts)

        setTopProducts(
          selectedProducts
            .slice(0, 3)
            .map(mapApiProductToCard)
        )
      })
      .catch((error) => {
        console.error(
          'Unable to load top products:',
          error
        )
      })

    return () => {
      mounted = false
    }
  }, [])

  /* =======================================================
     GSAP ANIMATION
  ======================================================= */

  useEffect(() => {
    const section = sectionRef.current
    const cardsContainer =
      cardsContainerRef.current

    if (!section || !cardsContainer) {
      return undefined
    }

    const prefersReducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches

    const cards =
      section.querySelectorAll(
        '[data-anim="card"]'
      )

    const images =
      section.querySelectorAll(
        '[data-anim="card-image"]'
      )

    const cardContents =
      section.querySelectorAll(
        '[data-anim="card-content"]'
      )

    const cardLines =
      section.querySelectorAll(
        '[data-anim="card-line"]'
      )

    const ribbons =
      section.querySelectorAll(
        '[data-anim="card-ribbon"]'
      )

    if (prefersReducedMotion) {
      gsap.set(
        [
          section.querySelectorAll(
            '[data-anim="eyebrow-line"]'
          ),
          section.querySelector(
            '[data-anim="heading"]'
          ),
          section.querySelector(
            '[data-anim="subtitle"]'
          ),
          cardsContainer,
          cards,
          images,
          cardContents,
          cardLines,
          ribbons,
        ],
        {
          clearProps: 'all',
          autoAlpha: 1,
          x: 0,
          y: 0,
          scale: 1,
          scaleX: 1,
          rotate: 0,
        }
      )

      return undefined
    }

    const context = gsap.context(() => {
      const timeline = gsap.timeline({
        paused: true,
        defaults: {
          ease: 'power3.out',
        },
      })

      timeline
        .from(
          '[data-anim="eyebrow-line"]',
          {
            scaleX: 0,
            opacity: 0,
            duration: 0.45,
            stagger: 0.08,
            transformOrigin:
              'center center',
          }
        )
        .from(
          '[data-anim="heading"]',
          {
            y: 22,
            opacity: 0,
            duration: 0.6,
          },
          '-=0.25'
        )
        .from(
          '[data-anim="subtitle"]',
          {
            y: 14,
            opacity: 0,
            duration: 0.5,
          },
          '-=0.32'
        )

      timeline.fromTo(
        cardsContainer,
        {
          y: 35,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.65,
        },
        0.35
      )

      timeline.fromTo(
        cards,
        {
          y: 60,
          autoAlpha: 0,
          scale: 0.95,
        },
        {
          y: 0,
          autoAlpha: 1,
          scale: 1,
          duration: 0.8,
          stagger: 0.12,
          ease: 'back.out(1.4)',
        },
        0.42
      )

      timeline.fromTo(
        ribbons,
        {
          y: -18,
          autoAlpha: 0,
          rotate: -8,
        },
        {
          y: 0,
          autoAlpha: 1,
          rotate: 0,
          duration: 0.5,
          stagger: 0.1,
          ease: 'back.out(2)',
        },
        0.6
      )

      timeline.fromTo(
        images,
        {
          scale: 1.12,
        },
        {
          scale: 1,
          duration: 1,
          stagger: 0.08,
          ease: 'power2.out',
        },
        0.48
      )

      timeline.fromTo(
        cardContents,
        {
          y: 22,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.6,
          stagger: 0.1,
        },
        0.62
      )

      timeline.fromTo(
        cardLines,
        {
          scaleX: 0,
        },
        {
          scaleX: 1,
          duration: 0.55,
          stagger: 0.1,
          transformOrigin:
            'left center',
          ease: 'power2.out',
        },
        0.72
      )

      ScrollTrigger.create({
        trigger: section,
        start: 'top 72%',

        onEnter: () => {
          timeline.restart()
        },

        invalidateOnRefresh: true,
      })

      ScrollTrigger.create({
        trigger: section,
        start: 'bottom 92%',

        onEnterBack: () => {
          timeline.restart()
        },

        invalidateOnRefresh: true,
      })
    }, section)

    const refreshTimer =
      window.setTimeout(() => {
        ScrollTrigger.refresh()
      }, 300)

    const handleWindowLoad = () => {
      ScrollTrigger.refresh()
    }

    window.addEventListener(
      'load',
      handleWindowLoad
    )

    return () => {
      window.clearTimeout(refreshTimer)

      window.removeEventListener(
        'load',
        handleWindowLoad
      )

      context.revert()
    }
  }, [])

  /* =======================================================
     JSX
  ======================================================= */

  return (
    <section
      ref={sectionRef}
      className="relative flex min-h-[100vh] flex-col justify-center overflow-hidden px-5 py-20 text-center sm:px-6 lg:py-24"
      style={{
        backgroundColor: NAV_BACKGROUND,
      }}
    >
      <div className="relative">

        {/* ================= HEADING ================= */}

       <SectionHeading
  title="TOP PRODUCTS"
  subtitle="Discover our exquisite range of jewelry categories"
  titleColor={MAROON_DEEP}
  lineColor={MAROON_DEEP}
  subtitleColor="rgba(43,0,0,0.68)"
/>
        <div
          ref={cardsContainerRef}
          className="container-x mt-10 max-w-6xl lg:mt-12"
        >

          {/* =================================================
              DESKTOP / LAPTOP
              #2        #1 FEATURED        #3

              IMPORTANT:
              Featured uses NEGATIVE TOP + BOTTOM margins.
              Therefore it grows equally upward and downward.
          ================================================= */}

          <div className="hidden md:grid md:grid-cols-3 md:items-center md:gap-5 xl:gap-6">

            {desktopProducts.map((product) => {
              const {
                id,
                name,
                displayName,
                image,
                subtitle,
                rating,
                reviews,
                ribbon,
                featured,
              } = product

              return (
                <article
                  key={id}
                  data-anim="card"
                  role="link"
                  tabIndex={0}
                  onClick={() => handleProductOpen(id)}
                  onKeyDown={(event) => handleProductKeyDown(event, id)}

                  className={`
                    group
                    relative
                    isolate
                    cursor-pointer
                    overflow-hidden
                    rounded-[24px]
                    text-left
                    transition-all
                    duration-500

                    ${
                      featured
                        ? `
                          z-10
                          md:-my-8
                          lg:-my-10
                          xl:-my-12
                        `
                        : `
                          z-0
                        `
                    }
                  `}

                  style={{
                    backgroundColor:
                      CARD_CREAM,

                    boxShadow: featured
                      ? `
                        0 42px 90px -28px rgba(212,175,55,0.52),
                        0 15px 35px -20px rgba(43,0,0,0.35),
                        0 0 0 1.5px rgba(212,175,55,0.72)
                      `
                      : `
                        0 22px 55px -28px rgba(43,0,0,0.4),
                        0 0 0 1px rgba(212,175,55,0.28)
                      `,
                  }}
                >

                  {/* GOLD GLOW */}

                  {featured && (
                    <div
                      className="
                        pointer-events-none
                        absolute
                        -inset-8
                        -z-10
                        rounded-[44px]
                        opacity-80
                        blur-3xl
                      "
                      style={{
                        background:
                          'radial-gradient(60% 70% at 50% 50%, rgba(212,175,55,0.38), transparent 72%)',
                      }}
                    />
                  )}

                  {/* ================= IMAGE ================= */}

                  <div
                    className={`
                      relative
                      overflow-hidden
                      rounded-[17px]

                      ${
                        featured
                          ? 'm-2.5'
                          : 'm-2.5'
                      }
                    `}

                    style={{
                      /*
                       * Featured image is also slightly taller.
                       * This prevents the extra featured height
                       * from looking like empty cream padding.
                       */
                      aspectRatio: featured
                        ? '1 / 1.08'
                        : '1.08 / 1',
                    }}
                  >
                    <RibbonBadge
                      number={ribbon}
                      size="desktop"
                    />

                    {/* BESTSELLER BADGE */}

                    {featured && (
                      <div
                        className="
                          absolute
                          right-3
                          top-3
                          z-20
                          flex
                          items-center
                          gap-1.5
                          rounded-full
                          px-3
                          py-1.5
                        "
                        style={{
                          backgroundColor:
                            'rgba(43,0,0,0.82)',

                          border:
                            '1px solid rgba(212,175,55,0.60)',

                          backdropFilter:
                            'blur(6px)',
                        }}
                      >
                        <Crown
                          className="h-3 w-3"
                          style={{
                            color: GOLD,
                          }}
                        />

                        <span
                          className="
                            text-[9px]
                            font-bold
                            uppercase
                            tracking-[0.22em]
                          "
                          style={{
                            color: GOLD_SOFT,
                          }}
                        >
                          Bestseller
                        </span>
                      </div>
                    )}

                    <img
                      data-anim="card-image"
                      src={image}
                      alt={`${name} jewellery collection`}
                      loading={
                        featured
                          ? 'eager'
                          : 'lazy'
                      }
                      decoding="async"
                      className="
                        h-full
                        w-full
                        object-cover
                        transition-transform
                        duration-700
                        group-hover:scale-[1.035]
                      "
                    />

                    {/* IMAGE OVERLAY */}

                    <div
                      className="
                        pointer-events-none
                        absolute
                        inset-0
                      "
                      style={{
                        background:
                          'linear-gradient(180deg, rgba(43,0,0,0.05) 0%, transparent 45%, rgba(20,0,0,0.18) 100%)',
                      }}
                    />
                  </div>

                  {/* ================= CONTENT ================= */}

                  <div
                    data-anim="card-content"

                    className={`
                      relative
                      px-4
                      pt-1
                      sm:px-5

                      ${
                        featured
                          ? 'pb-6 lg:pb-7'
                          : 'pb-4'
                      }
                    `}
                  >

                    <h3
                      className={`
                        font-serif
                        font-semibold
                        leading-tight

                        ${
                          featured
                            ? 'text-[23px] lg:text-[26px]'
                            : 'text-[22px] lg:text-[24px]'
                        }
                      `}
                      style={{
                        color: MAROON_DEEP,
                      }}
                    >
                      {displayName || name}
                    </h3>

                    {/* GOLD LINE */}

                    <div
                      data-anim="card-line"
                      className="
                        my-2.5
                        h-px
                        w-9
                        origin-left
                      "
                      style={{
                        backgroundColor:
                          GOLD,
                      }}
                    />

                    {/* DESCRIPTION */}

                    <p
                      className={`
                        leading-relaxed

                        ${
                          featured
                            ? 'text-xs lg:text-[12.5px]'
                            : 'text-[11px] lg:text-xs'
                        }
                      `}
                      style={{
                        color:
                          'rgba(43,0,0,0.55)',
                      }}
                    >
                      {subtitle}
                    </p>

                    {/* RATING + BUTTON */}

                    <div
                      className={`
                        flex
                        items-center
                        justify-between
                        gap-3

                        ${
                          featured
                            ? 'mt-5'
                            : 'mt-3.5'
                        }
                      `}
                    >

                      {/* RATING */}

                      <div className="flex min-w-0 items-center gap-1">

                        <StarRow
                          rating={rating}
                          size={
                            featured
                              ? 'h-3.5 w-3.5'
                              : 'h-3 w-3'
                          }
                        />

                        <span
                          className="
                            ml-1
                            text-[11px]
                            font-semibold
                          "
                          style={{
                            color:
                              MAROON_DEEP,
                          }}
                        >
                          {rating
                            ? rating.toFixed(
                                1
                              )
                            : 'New'}
                        </span>

                        <span
                          className="text-[10px]"
                          style={{
                            color:
                              'rgba(43,0,0,0.45)',
                          }}
                        >
                          ({reviews})
                        </span>
                      </div>

                      {/* BUTTON */}

                    <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          handleProductOpen(id)
                        }}

                        className={`
                          inline-flex
                          shrink-0
                          items-center
                          gap-1.5
                          rounded-full
                          font-semibold
                          uppercase
                          tracking-[0.11em]
                          transition-all
                          duration-300

                          hover:-translate-y-0.5

                          ${
                            featured
                              ? `
                                px-4
                                py-2.5
                                text-[9.5px]
                                lg:px-5
                              `
                              : `
                                px-3.5
                                py-2
                                text-[9px]
                                lg:px-4
                                lg:text-[9.5px]
                              `
                          }
                        `}

                        style={
                          featured
                            ? {
                                backgroundColor:
                                  MAROON_DEEP,

                                color:
                                  CREAM,
                              }
                            : {
                                backgroundColor:
                                  'transparent',

                                color:
                                  MAROON_DEEP,

                                border: `1.3px solid ${MAROON_DEEP}`,
                              }
                        }
                      >
                        {featured
                          ? 'Shop Now'
                          : 'Explore Now'}

                        <span
                          className={`
                            grid
                            place-items-center
                            rounded-full

                            ${
                              featured
                                ? 'h-6 w-6'
                                : 'h-5 w-5'
                            }
                          `}
                          style={{
                            backgroundColor:
                              featured
                                ? GOLD
                                : MAROON_DEEP,

                            color: featured
                              ? MAROON_DEEP
                              : CREAM,
                          }}
                        >
                          <ArrowUpRight
                            className="h-3 w-3"
                          />
                        </span>
                      </button>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>

          {/* =================================================
              MOBILE
          ================================================= */}

          <div
            className="
              mx-auto
              flex
              w-[calc(100%+32px)]
              max-w-[480px]
              -translate-x-4
              flex-col
              gap-4
              md:hidden
            "
          >
            {topProducts.map((product) => {
              const {
                id,
                name,
                displayName,
                image,
                rating,
                reviews,
                ribbon,
                featured,
                features,
              } = product

              return (
                <article
                  key={id}
                  data-anim="card"
                  role="link"
                  tabIndex={0}
                  onClick={() => handleProductOpen(id)}
                  onKeyDown={(event) => handleProductKeyDown(event, id)}
                  className={`
                    relative
                    isolate
                    cursor-pointer
                    overflow-hidden
                    rounded-[20px]
                    p-3
                    text-left
                    ${featured ? 'min-h-[250px] sm:min-h-[270px]' : ''}
                  `}
                  style={{
                    backgroundColor:
                      CARD_CREAM,

                    border: `1px solid ${
                      featured
                        ? 'rgba(212,175,55,0.6)'
                        : 'rgba(212,175,55,0.3)'
                    }`,

                    boxShadow: featured
                      ? '0 18px 42px -18px rgba(212,175,55,0.4)'
                      : '0 14px 32px -20px rgba(43,0,0,0.3)',
                  }}
                >

                  <div className="flex gap-3.5">

                    {/* MOBILE IMAGE */}

                    <div
                      className={`
                        relative
                        shrink-0
                        overflow-hidden
                        rounded-[14px]

                        ${
                          featured
                            ? `
                              h-[142px]
                              w-[116px]
                              sm:h-[154px]
                              sm:w-[130px]
                            `
                            : `
                              h-[108px]
                              w-[108px]
                              sm:h-[122px]
                              sm:w-[122px]
                            `
                        }
                      `}
                    >
                      <RibbonBadge
                        number={ribbon}
                        size="mobile"
                      />

                      <img
                        data-anim="card-image"
                        src={image}
                        alt={`${name} jewellery collection`}
                        loading={
                          featured
                            ? 'eager'
                            : 'lazy'
                        }
                        decoding="async"
                        className="
                          h-full
                          w-full
                          object-cover
                        "
                      />
                    </div>

                    {/* MOBILE CONTENT */}

                    <div
                      data-anim="card-content"
                      className="
                        flex
                        min-w-0
                        flex-1
                        flex-col
                      "
                    >
                      <div
                        className="
                          flex
                          items-start
                          justify-between
                          gap-2
                        "
                      >
                        <h3
                          className="
                            font-serif
                            text-[17px]
                            font-semibold
                            leading-tight
                          "
                          style={{
                            color:
                              MAROON_DEEP,
                          }}
                        >
                          {displayName}
                        </h3>

                        <ChevronRight
                          className="
                            mt-1
                            h-4
                            w-4
                            shrink-0
                          "
                          style={{
                            color: GOLD,
                          }}
                        />
                      </div>



                      {/* MOBILE RATING */}

                      <div className="mt-1.5 flex items-center gap-1">

                        <StarRow
                          rating={rating}
                          size="h-3 w-3"
                        />

                        <span
                          className="
                            ml-1
                            text-[10.5px]
                            font-semibold
                          "
                          style={{
                            color:
                              MAROON_DEEP,
                          }}
                        >
                          {rating
                            ? rating.toFixed(
                                1
                              )
                            : 'New'}
                        </span>

                        <span
                          className="text-[10.5px]"
                          style={{
                            color:
                              'rgba(43,0,0,0.45)',
                          }}
                        >
                          ({reviews})
                        </span>
                      </div>

                      <div
                        data-anim="card-line"
                        className="
                          mt-2
                          h-px
                          w-full
                          origin-left
                        "
                        style={{
                          backgroundColor:
                            'rgba(212,175,55,0.3)',
                        }}
                      />

                      {/* MOBILE FEATURES */}

                      <div
                        className="
                          mt-2
                          flex
                          flex-wrap
                          items-center
                          gap-x-2.5
                          gap-y-1
                        "
                      >
                        {features.map(
                          ({
                            icon: Icon,
                            label,
                          }) => (
                            <span
                              key={label}
                              className="
                                flex
                                items-center
                                gap-1
                                text-[8.5px]
                                font-medium
                                leading-none
                              "
                              style={{
                                color:
                                  'rgba(43,0,0,0.62)',
                              }}
                            >
                              <Icon
                                className="h-2.5 w-2.5"
                                style={{
                                  color:
                                    GOLD,
                                }}
                              />

                              {label}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  {/* MOBILE BUTTON */}

                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      handleProductOpen(id)
                    }}
                    className="
                      mt-3
                      inline-flex
                      w-full
                      items-center
                      justify-center
                      gap-1.5
                      rounded-full
                      px-4
                      py-2
                      text-[10px]
                      font-semibold
                      uppercase
                      tracking-[0.1em]
                      transition-all
                      duration-300
                    "
                    style={
                      featured
                        ? {
                            backgroundColor:
                              MAROON_DEEP,

                            color:
                              CREAM,
                          }
                        : {
                            backgroundColor:
                              'transparent',

                            color:
                              MAROON_DEEP,

                            border: `1.3px solid ${MAROON_DEEP}`,
                          }
                    }
                  >
                    {featured
                      ? 'Shop Now'
                      : 'Explore Now'}

                    <ArrowUpRight className="h-3 w-3" />
                  </button>
                </article>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
